/**
 * Token-related authentication utilities
 */
import { AuthResponse, getLocalStorage, handleLogout, saveLocalStorage } from "@elit-hair/elit-tools";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import axios from "axios";

import { getAxiosInstanceForUrl } from "@/constants";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { authServiceEndpoints } from "@/queries/auth.queries";

/**
 * Backend logout hook to be used with AuthContextProvider.beforeLogout
 * Makes API call to invalidate tokens on backend before frontend logout
 * Returns true to continue the default logout flow even if backend call fails
 */
export const handleAuthSvcLogout = async (): Promise<boolean> => {
    try {
        const auth = getLocalStorage<AuthResponse | null>(LOCAL_STORAGE_KEYS.AUTH.key, LOCAL_STORAGE_KEYS.AUTH.default);
        if (auth?.accessToken || auth?.refreshToken) {
            await axios.post(authServiceEndpoints.logout, {
                accessToken: auth.accessToken,
                refreshToken: auth.refreshToken,
            });
        }
        return true; // Continue with frontend logout
    } catch (error) {
        console.error("❌ Backend logout failed:", error);
        return true; // Still continue with frontend logout even if backend fails
    }
};

/**
 * Handles refresh token request to get new tokens
 */
export const handleRefreshToken = async () => {
    const auth = getLocalStorage<AuthResponse | null>(LOCAL_STORAGE_KEYS.AUTH.key, LOCAL_STORAGE_KEYS.AUTH.default);
    if (!auth?.refreshToken) {
        console.log("❌ No refresh token found in localStorage - logging out");
        handleLogout();
        throw new Error("No refresh token found");
    }

    try {
        console.log("🔄 Attempting to refresh token...");
        // Use direct axios.post() without interceptors to avoid infinite loops
        // If we used authSvcAxiosInstance (which has interceptors), a 401 error would trigger
        // the response interceptor, causing another refresh attempt and creating an infinite loop
        const response = await axios.post(authServiceEndpoints.refreshToken, {
            refreshToken: auth.refreshToken,
        });
        return response.data;
    } catch (error) {
        // If refresh token fails with 401, logout immediately
        if (error instanceof AxiosError && error.response?.status === 401) {
            console.log("🔄 Refresh token expired, logging out...");
            handleLogout();
        }

        throw error;
    }
};

/**
 * Updates auth tokens in localStorage with new tokens
 */
export const updateAuthTokens = (newTokens: { accessToken: string; refreshToken: string }) => {
    const auth = getLocalStorage<AuthResponse | null>(LOCAL_STORAGE_KEYS.AUTH.key, LOCAL_STORAGE_KEYS.AUTH.default);
    if (auth) {
        auth.accessToken = newTokens.accessToken;
        auth.refreshToken = newTokens.refreshToken;
        saveLocalStorage(LOCAL_STORAGE_KEYS.AUTH.key, auth);
        console.log("✅ Auth tokens updated successfully");
    }
};

/**
 * Handles the complete token refresh flow
 *
 * STEP 3 in the refresh flow - Orchestrates the entire refresh process
 *
 * FLOW:
 * 1. Call refresh token API to get new tokens
 * 2. Update tokens in localStorage
 * 3. Return success/failure status
 * 4. On failure, perform logout
 *
 * @returns Promise<boolean> - true if refresh succeeded, false if failed
 */
export const handleTokenRefreshFlow = async (): Promise<boolean> => {
    try {
        console.log("🔄 Starting token refresh flow...");

        // STEP 1: Get new tokens from refresh endpoint
        const newTokens = await handleRefreshToken();

        // STEP 2: Update tokens in localStorage
        updateAuthTokens(newTokens);

        console.log("✅ Token refresh flow completed successfully");
        return true;
    } catch (error) {
        console.log("❌ Token refresh flow failed:", error);

        // STEP 3: On failure, return false (logout handled by interceptor for refresh token errors)
        return false;
    }
};

/**
 * Retries the original request with new authorization token
 *
 * STEP 6 in the refresh flow - Final step that retries the failed request
 *
 * FLOW:
 * 1. Update the request config with new Bearer token
 * 2. Dynamically select the correct axios instance based on URL
 * 3. Retry the original request with new token
 *
 * @param originalConfig - The original failed request configuration
 * @param newToken - The new access token to use
 * @returns Promise with the retried request response
 */
export const retryRequestWithNewToken = (originalConfig: InternalAxiosRequestConfig, newToken: string) => {
    if (originalConfig) {
        // STEP 1: Update request headers with new Bearer token
        originalConfig.headers.Authorization = `Bearer ${newToken}`;

        // STEP 2: Dynamically select the correct axios instance based on the request URL
        const url = originalConfig.url || originalConfig.baseURL || "";
        const axiosInstance = getAxiosInstanceForUrl(url);

        // STEP 3: Retry the original request with new token
        return axiosInstance.request(originalConfig);
    }
    return Promise.reject(new Error("No original config available"));
};

/**
 * Gets the current auth token from localStorage
 */
export const getCurrentAuthToken = (): string => {
    return (
        getLocalStorage<AuthResponse | null>(LOCAL_STORAGE_KEYS.AUTH.key, LOCAL_STORAGE_KEYS.AUTH.default)
            ?.accessToken || ""
    );
};

/**
 * Retries a request with the current auth token
 */
export const retryRequestWithCurrentToken = (config: InternalAxiosRequestConfig) => {
    return retryRequestWithNewToken(config, getCurrentAuthToken());
};

// Race condition protection for refresh/logout
// These variables ensure only one refresh attempt happens at a time
// All concurrent 401 requests will wait for the same refresh promise
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Resets the refresh state variables
 */
export const resetRefreshState = () => {
    isRefreshing = false;
    refreshPromise = null;
};

/**
 * Gets the current refresh state
 */
export const getRefreshState = () => ({
    isRefreshing,
    refreshPromise,
});

/**
 * Sets the refresh state
 */
export const setRefreshState = (refreshing: boolean, promise: Promise<boolean> | null) => {
    isRefreshing = refreshing;
    refreshPromise = promise;
};
